import { useEffect, useState, useSyncExternalStore } from "react";

/** What the arriving canvas has to do about the first message. */
export type DispatchOutcome =
  | { kind: "sent"; sessionId: string }
  /** The session exists but is empty: its composer takes the prompt. */
  | { kind: "post-failed"; sessionId: string }
  /** No session at all: the canvas opens its own and takes the prompt. */
  | { kind: "session-failed" };

/** A header the canvas places at the origin before the first message goes. */
export interface PendingHeader {
  file: string;
  html: string;
  width: number;
  height: number;
  /**
   * Called as the header goes on the canvas (true), or when it could not be
   * placed (false). False back means the message already went without it
   * and the header must stay off.
   */
  settle: (placed: boolean) => boolean;
}

export interface PendingDispatch {
  canvasId: string;
  prompt: string;
  /** The effort the home sent; the canvas's picker starts there too. */
  reasoningEffort?: string;
  header?: PendingHeader;
  outcome: Promise<DispatchOutcome>;
}

// Module-level because it outlives the surface that started it: the
// navigation unmounts the composer. Read through useSyncExternalStore so the
// React Compiler cannot memoize a render-time read of a live variable.
let pending: PendingDispatch | null = null;
const listeners = new Set<() => void>();

export function publishCanvasDispatch(next: PendingDispatch | null) {
  pending = next;
  for (const listener of listeners) listener();
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function getPending(): PendingDispatch | null {
  return pending;
}

/**
 * The dispatch bound for this canvas, claimed once and held: clearing the
 * store is what stops a later visit to the same canvas replaying it, and
 * holding it locally is what survives that clear.
 */
export function useClaimedDispatch(
  canvasId: string | undefined,
): PendingDispatch | null {
  const current = useSyncExternalStore(subscribe, getPending);
  const [claimed, setClaimed] = useState<PendingDispatch | null>(null);
  useEffect(() => {
    if (
      claimed?.canvasId === canvasId ||
      !canvasId ||
      current?.canvasId !== canvasId
    )
      return;
    setClaimed(current);
    publishCanvasDispatch(null);
  }, [canvasId, current, claimed]);
  return claimed?.canvasId === canvasId
    ? claimed
    : current?.canvasId === canvasId
      ? current
      : null;
}

export function resetDispatchForTest() {
  pending = null;
}
